import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, ButtonGroup, Input, Spinner } from "reactstrap";
import {
  approveStudioOwnershipClaim,
  listStudioOwnershipClaims,
  rejectStudioOwnershipClaim,
} from "../../services/studioCollectorAccessApi";

type ClaimRow = {
  id: string;
  status: string;
  note?: string;
  invoiceNumber?: string;
  rejectReason?: string;
  closedReason?: string;
  createdAt?: string;
  decidedAt?: string;
  decidedByName?: string;
  productUniqueId?: string;
  productTitle?: string;
  productPhotoUrl?: string;
  collectorDisplayName?: string;
  collectorPhone?: string;
  sale?: {
    partyName?: string;
    partyPhone?: string;
    invoiceNumber?: string;
    memoCode?: string;
    soldAt?: string;
  };
};

const ERRORS: Record<string, string> = {
  ANOTHER_OWNER: "Another collector already owns this piece through this sale.",
  SALE_CHANGED: "This sale was returned or replaced. The claim no longer applies.",
  CLAIM_ALREADY_DECIDED: "Someone already decided this claim.",
};

const STATUS_COLOR: Record<string, string> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "danger",
  CLOSED: "secondary",
};

const CLOSED_TEXT: Record<string, string> = {
  SALE_CHANGED: "Closed: the sale was returned or replaced",
  ANOTHER_CLAIM_APPROVED: "Closed: another claim was approved",
  MERGED: "Closed: the collector merged accounts",
};

const when = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : "");

/** Buyers who say they bought a piece this studio sold, when their phone didn't match the sale. */
const StudioOwnershipClaims: React.FC = () => {
  const [filter, setFilter] = useState<"PENDING" | "ALL">("PENDING");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ClaimRow[]>([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejecting, setRejecting] = useState<{ id: string; reason: string } | null>(null);

  const load = (status: string) => {
    setError("");
    return listStudioOwnershipClaims(status)
      .then((res: any) => {
        if (res?.success === false) throw new Error(res?.message || "Could not load claims");
        setItems(res?.data?.items || []);
      })
      .catch((e: any) => setError(e?.message || "Could not load claims"));
  };

  useEffect(() => {
    setLoading(true);
    load(filter).finally(() => setLoading(false));
  }, [filter]);

  const act = async (id: string, call: () => Promise<any>) => {
    setBusyId(id);
    setError("");
    try {
      const res = await call();
      if (res?.success === false) throw new Error(res?.message || "Action failed");
      setRejecting(null);
      await load(filter);
    } catch (e: any) {
      setError(ERRORS[e?.message] || e?.message || "Action failed");
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h2 className="h5 mb-0">Ownership claims</h2>
        <ButtonGroup size="sm">
          <Button color="dark" outline={filter !== "PENDING"} onClick={() => setFilter("PENDING")}>Waiting</Button>
          <Button color="dark" outline={filter !== "ALL"} onClick={() => setFilter("ALL")}>All</Button>
        </ButtonGroup>
      </div>
      <p className="text-muted small">
        A buyer can claim a piece you sold when their login number doesn't match the number on the sale.
        Compare their details with the sale, then approve to add the piece to their collection as owned.
      </p>
      {error && <Alert color="danger">{error}</Alert>}
      {loading ? (
        <div className="text-center py-3"><Spinner size="sm" /></div>
      ) : items.length === 0 ? (
        <p className="text-muted">{filter === "PENDING" ? "No claims waiting." : "No claims yet."}</p>
      ) : (
        <ul className="list-unstyled">
          {items.map((row) => {
            const busy = busyId === row.id;
            const isRejecting = rejecting?.id === row.id;
            return (
              <li key={row.id} className="border rounded p-3 mb-3">
                <div className="d-flex gap-3 align-items-start">
                  {row.productPhotoUrl && (
                    <img src={row.productPhotoUrl} alt="" width={72} height={72} style={{ objectFit: "cover", borderRadius: 8 }} />
                  )}
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className="fw-semibold">{row.productTitle || row.productUniqueId || "Piece"}</span>
                      <Badge color={STATUS_COLOR[row.status] || "secondary"}>{row.status}</Badge>
                    </div>
                    <small className="text-muted d-block">Claimed {when(row.createdAt)}</small>
                  </div>
                </div>

                <div className="row g-2 mt-2 small">
                  <div className="col-12 col-md-6">
                    <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Claimed by</div>
                    <div>{row.collectorDisplayName?.trim() || "Collector"}</div>
                    <div>{row.collectorPhone || "—"}</div>
                    {row.invoiceNumber && <div>Invoice they hold: {row.invoiceNumber}</div>}
                    {row.note && <div className="fst-italic" style={{ whiteSpace: "pre-wrap" }}>“{row.note}”</div>}
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Sale on record</div>
                    <div>{row.sale?.partyName || "—"}</div>
                    <div>{row.sale?.partyPhone || "No phone on file"}</div>
                    <div>
                      {[row.sale?.invoiceNumber && `Invoice ${row.sale.invoiceNumber}`, row.sale?.memoCode, when(row.sale?.soldAt)]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                </div>

                {row.status === "REJECTED" && row.rejectReason && (
                  <p className="small text-muted mt-2 mb-0">Rejected{row.decidedByName ? ` by ${row.decidedByName}` : ""}: {row.rejectReason}</p>
                )}
                {row.status === "APPROVED" && (
                  <p className="small text-muted mt-2 mb-0">Approved{row.decidedByName ? ` by ${row.decidedByName}` : ""} {when(row.decidedAt)}</p>
                )}
                {row.status === "CLOSED" && (
                  <p className="small text-muted mt-2 mb-0">{CLOSED_TEXT[row.closedReason || ""] || "Closed"}</p>
                )}

                {row.status === "PENDING" && !isRejecting && (
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <Button size="sm" color="dark" disabled={busy} onClick={() => act(row.id, () => approveStudioOwnershipClaim(row.id))}>
                      {busy ? "Saving…" : "Approve"}
                    </Button>
                    <Button size="sm" outline color="secondary" disabled={busy} onClick={() => setRejecting({ id: row.id, reason: "" })}>
                      Reject
                    </Button>
                  </div>
                )}
                {isRejecting && rejecting && (
                  <div className="mt-3">
                    <Input
                      type="textarea"
                      rows={2}
                      maxLength={300}
                      placeholder="Reason the collector will see"
                      value={rejecting.reason}
                      onChange={(e) => setRejecting({ id: row.id, reason: e.target.value })}
                    />
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        color="danger"
                        disabled={busy || !rejecting.reason.trim()}
                        onClick={() => act(row.id, () => rejectStudioOwnershipClaim(row.id, rejecting.reason.trim()))}
                      >
                        {busy ? "Saving…" : "Reject claim"}
                      </Button>
                      <Button size="sm" outline color="secondary" disabled={busy} onClick={() => setRejecting(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default StudioOwnershipClaims;
